import { useCallback, useContext, useEffect, useState } from 'react';
import {
  AnalyticsRange,
  useDashboardContext,
} from '../../../contexts/DashboardContext';
import { UserContext } from '../../../contexts/UserContext';
import { getWebsiteOnViewAnalytics } from '../../../services/AnalyticsService';
import { analyticsRangeToDates, PREVIEW_ROOT } from '../../../utils';

export interface UseEditorAnalyticsReturn {
  isLoadingAnalytics: boolean;
  analytics:
    | {
        websiteId: string;
        analyticsRange: AnalyticsRange;
        [key: string]: any;
      }
    | undefined;
}

interface UseEditorAnalyticsProps {
  id: string;
  dynamicPageId: string | undefined;
  websiteId: string;
  showAnalytics: boolean;
  previewMode: boolean;
  sendMessageToIframe: (message: any) => void;
}

export function useEditorAnalytics({
  id,
  dynamicPageId,
  websiteId,
  showAnalytics,
  previewMode,
  sendMessageToIframe,
}: UseEditorAnalyticsProps): UseEditorAnalyticsReturn {
  const { analyticsRange } = useDashboardContext();
  const { selectedAccount } = useContext(UserContext);

  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false);
  const [analytics, setAnalytics] = useState<
    | {
        websiteId: string;
        analyticsRange: AnalyticsRange;
        [key: string]: any;
      }
    | undefined
  >();

  // Fetch analytics when showAnalytics is enabled in preview mode
  useEffect(() => {
    if (!previewMode || !showAnalytics) return;
    if (!selectedAccount || !dynamicPageId || !websiteId) return;

    // Check if we already have cached analytics for this website and range
    if (
      analytics &&
      analytics.websiteId === websiteId &&
      analytics.analyticsRange === analyticsRange
    ) {
      return;
    }

    const { startDate, endDate } = analyticsRangeToDates(analyticsRange);
    setIsLoadingAnalytics(true);

    let relevant = true;
    getWebsiteOnViewAnalytics(
      selectedAccount.id,
      dynamicPageId,
      websiteId,
      startDate,
      endDate,
    )
      .then((res) => {
        if (!relevant) return;
        setAnalytics({ ...res, analyticsRange, websiteId });
      })
      .finally(() => {
        if (!relevant) return;
        setIsLoadingAnalytics(false);
      });

    return () => {
      relevant = false;
    };
  }, [
    websiteId,
    selectedAccount,
    dynamicPageId,
    previewMode,
    showAnalytics,
    analyticsRange,
    analytics,
  ]);

  // Send analytics to iframe or hide them
  useEffect(() => {
    if (!showAnalytics || !previewMode) {
      sendMessageToIframe({ eventName: 'hideAnalytics' });
      return;
    }

    if (analytics) {
      sendMessageToIframe({ eventName: 'showAnalytics', analytics });
    }
  }, [showAnalytics, analytics, previewMode, sendMessageToIframe]);

  // Handle ready event from iframe to resend analytics
  const handleAnalyticsReadyEvent = useCallback(
    (event: MessageEvent<string>) => {
      if (event.origin !== PREVIEW_ROOT) return;
      if (!dynamicPageId) return;

      const iframe = (document.getElementById(id) as HTMLIFrameElement)
        ?.contentWindow;
      if (event.source !== iframe) return;

      if (typeof event.data !== 'string' || !event.data.startsWith('{')) return;

      try {
        const data = JSON.parse(event.data);
        if (data.eventName === 'ready' && showAnalytics && analytics) {
          sendMessageToIframe({ eventName: 'showAnalytics', analytics });
        }
      } catch (e) {
        console.error('[useEditorAnalytics] failed to parse event data', e);
      }
    },
    [id, dynamicPageId, showAnalytics, analytics, sendMessageToIframe],
  );

  useEffect(() => {
    window.addEventListener('message', handleAnalyticsReadyEvent);

    return () => {
      window.removeEventListener('message', handleAnalyticsReadyEvent);
    };
  }, [handleAnalyticsReadyEvent]);

  // Clear analytics cache when websiteId changes
  useEffect(() => {
    setAnalytics(undefined);
  }, [websiteId]);

  return {
    isLoadingAnalytics,
    analytics,
  };
}
